import { Typography } from '@mui/material'
import type { GridRowParams } from '@mui/x-data-grid'
import { GRID_DETAIL_PANEL_TOGGLE_COL_DEF } from '@mui/x-data-grid-pro'
import { DateTime } from 'luxon'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import DataGridDetailPanelToggle from '@/components/dataDisplay/DataGridDetailPanelToggle'
import { GridActionCell } from '@/features/resource/components/rampCurves/GridActionCell'
import RampCurveDetails from '@/features/resource/components/rampCurves/RampCurveDetails'
import RampCurveForm from '@/features/resource/components/rampCurves/RampCurveForm'
import { StyledCustomDataGrid } from '@/features/resource/components/steering/StyledCustomDataGrid'
import type { RampCurve, RampCurveDirection, UpdateRampCurveRequest } from '@/features/resource/types'

export interface RampCurvesDataGridProps {
  rampCurves: RampCurve[]
  onUpdateRampCurve: (rampCurveId: string, data: UpdateRampCurveRequest) => void
  onDeleteRampCurve: (rampCurveId: string) => void
}

export const RampCurvesDataGrid = ({ rampCurves, onUpdateRampCurve, onDeleteRampCurve }: RampCurvesDataGridProps) => {
  const { t } = useTranslation()
  const [editingRampCurve, setEditingRampCurve] = useState<RampCurve | null>(null)

  const columns = [
    {
      ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
      renderCell: (params) => <DataGridDetailPanelToggle params={params} />,
      sortable: false,
      flex: 0.4,
    },
    {
      field: 'name',
      headerName: t('resources.ramp_curves.table.name'),
      flex: 2,
      renderCell: (params) => (
        <Typography
          sx={{
            fontWeight: 500,
            display: 'flex',
            alignItems: 'center',
            height: '100%',
          }}
        >
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'meteringPortId',
      headerName: t('resources.ramp_curves.table.metering_port_id'),
      flex: 2,
      renderCell: (params) => params.value || '-',
    },
    {
      field: 'verifiedAt',
      headerName: t('resources.ramp_curves.table.verified_at'),
      flex: 2,
      valueFormatter: (value: string) => (value ? DateTime.fromISO(value).toFormat('dd/MM/yyyy, HH:mm') : '-'),
    },
    {
      field: 'direction',
      headerName: t('resources.ramp_curves.table.direction'),
      flex: 1,
      renderCell: (params) =>
        t(`resources.ramp_curves.direction.${params.value.toLowerCase() as Lowercase<RampCurveDirection>}`),
    },
    {
      field: 'actions',
      headerName: '',
      flex: 0.3,
      sortable: false,
      renderCell: (params) => (
        <GridActionCell
          params={params}
          onEdit={() => {
            setEditingRampCurve(params.row)
          }}
        />
      ),
    },
  ]

  return (
    <StyledCustomDataGrid
      disableColumnSorting
      hideFooter
      hideFooterPagination
      hideFooterRowCount
      columns={columns}
      getDetailPanelContent={({ row }: GridRowParams<RampCurve>) =>
        editingRampCurve?.id === row.id ? (
          <RampCurveForm
            initialData={row}
            onCancel={() => setEditingRampCurve(null)}
            onDelete={() => onDeleteRampCurve(row.id)}
            onSave={(data) => {
              onUpdateRampCurve(row.id, data)
              setEditingRampCurve(null)
            }}
          />
        ) : (
          <RampCurveDetails rampCurve={row} onEdit={() => setEditingRampCurve(row)} />
        )
      }
      getDetailPanelHeight={() => 'auto'}
      getRowId={(row: RampCurve) => row.id}
      includeWrapper={false}
      rows={rampCurves}
    />
  )
}
